import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, ShieldCheck, Search } from "lucide-react";

export const SECTIONS = [
  ["dashboard", "Dashboard"],
  ["vehicles", "Vehicles"],
  ["drivers", "Drivers"],
  ["stores", "Stores"],
  ["routes", "Route Planner"],
  ["costs", "Costs"],
  ["fines", "PCN Fines"],
  ["insurance", "Insurance"],
  ["suppliers", "Suppliers"],
];
const LEVELS = [["none", "No access"], ["view", "View only"], ["edit", "View + Edit"]];
const DEFAULT_PERMS = Object.fromEntries(SECTIONS.map(([k]) => [k, "none"]));
const EMPTY = { name: "", email: "", password: "" };
const inputCls = "mt-1.5 rounded-none bg-background border-border h-9";
const LEVEL_ACTIVE = { none: "bg-muted text-foreground", view: "bg-[#0A84FF] text-white", edit: "bg-[#FF3B30] text-white" };

const PermissionMatrix = ({ perms, onChange }) => (
  <div className="border border-border divide-y divide-border/60" data-testid="permission-matrix">
    {SECTIONS.map(([key, label]) => (
      <div key={key} className="flex items-center justify-between px-3 py-1.5">
        <span className="text-sm">{label}</span>
        <div className="flex border border-border">
          {LEVELS.map(([lv, lvLabel]) => (
            <button
              key={lv}
              type="button"
              data-testid={`perm-${key}-${lv}`}
              onClick={() => onChange({ ...perms, [key]: lv })}
              className={`px-3 py-1 text-[10px] uppercase tracking-widest font-bold transition-colors duration-150 ${
                (perms[key] || "none") === lv ? LEVEL_ACTIVE[lv] : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {lvLabel}
            </button>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const accessSummary = (u) => {
  if (u.role === "admin") return null;
  const vals = Object.values(u.permissions || {});
  const edits = vals.filter((v) => v === "edit").length;
  const views = vals.filter((v) => v === "view").length;
  if (!edits && !views) return "No access";
  return [edits && `${edits} edit`, views && `${views} view`].filter(Boolean).join(" · ");
};

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [perms, setPerms] = useState(DEFAULT_PERMS);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditUser(null); setForm(EMPTY); setPerms(DEFAULT_PERMS); setOpen(true); };
  const openEdit = (u) => {
    setEditUser(u);
    setForm({ name: u.name, email: u.email, password: "" });
    setPerms({ ...DEFAULT_PERMS, ...(u.permissions || {}) });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      if (editUser) {
        await api.put(`/users/${editUser.id}`, { name: form.name, permissions: perms, password: form.password || null });
        toast.success("User updated");
      } else {
        if (!form.email.trim()) return toast.error("Email is required");
        if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
        await api.post("/users", { ...form, permissions: perms });
        toast.success(`User ${form.name} added`);
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { disabled: !u.disabled });
      toast.success(u.disabled ? `${u.name} enabled` : `${u.name} disabled`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.name} (${u.email})?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const shown = users.filter((u) => [u.name, u.email, u.role].join(" ").toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="p-8" data-testid="users-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">USERS</h1>
          <p className="text-muted-foreground text-sm mt-1">{users.length} users — control who can view or edit each section</p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="user-search-input" placeholder="Search name, email…" value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-none bg-card border-border pl-9 w-64 h-10" />
          </div>
          <Button data-testid="add-user-button" onClick={openAdd} className="rounded-none bg-[#FF3B30] hover:bg-[#ff554b] font-bold uppercase tracking-widest text-xs h-10">
            <Plus size={15} className="mr-1" /> Add user
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border rounded-none text-foreground max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading font-black tracking-tighter text-2xl">{editUser ? "EDIT USER" : "ADD USER"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">Name *</Label>
              <Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">Email *</Label>
              <Input data-testid="user-email-input" value={form.email} disabled={!!editUser} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">{editUser ? "Reset password (leave blank to keep current)" : "Password *"}</Label>
              <Input data-testid="user-password-input" type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">Section permissions</Label>
              <div className="mt-1.5">
                <PermissionMatrix perms={perms} onChange={setPerms} />
              </div>
            </div>
            <Button data-testid="save-user-button" onClick={save} disabled={saving} className="w-full rounded-none bg-[#FF3B30] hover:bg-[#ff554b] font-bold uppercase tracking-widest text-xs h-11">
              {saving ? "Saving…" : editUser ? "Save changes" : "Create user"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="text-left border-b border-border">
              {["Name", "Email", "Role", "Access", "Active", ""].map((h, i) => (
                <th key={i} className="px-4 py-3 uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.email}`} className={`border-b border-border/60 hover:bg-muted/60 ${u.disabled ? "opacity-50" : ""}`}>
                <td className="px-4 py-3 font-semibold whitespace-nowrap">{u.name || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{u.email}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {u.role === "admin" ? (
                    <span className="inline-flex items-center gap-1.5 border border-[#FF3B30]/40 bg-[#FF3B30]/10 text-[#FF3B30] px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold"><ShieldCheck size={11} /> Admin</span>
                  ) : (
                    <span className="border border-border bg-muted/60 text-muted-foreground px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold">Staff</span>
                  )}
                </td>
                <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">{u.role === "admin" ? "Full access" : accessSummary(u)}</td>
                <td className="px-4 py-3">
                  {u.role === "admin" ? (
                    <span className="text-xs text-muted-foreground/60">Always</span>
                  ) : (
                    <Switch data-testid={`user-active-switch-${u.email}`} checked={!u.disabled} onCheckedChange={() => toggleActive(u)} />
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {u.role !== "admin" && (
                    <div className="flex items-center gap-2">
                      <button data-testid={`edit-user-${u.email}`} onClick={() => openEdit(u)} className="text-muted-foreground/70 hover:text-foreground transition-colors duration-150"><Pencil size={14} /></button>
                      <button data-testid={`delete-user-${u.email}`} onClick={() => remove(u)} className="text-muted-foreground/70 hover:text-[#FF3B30] transition-colors duration-150"><Trash2 size={14} /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground/70">No users match your search.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
